import * as React from 'react';
import { cn } from '../../../lib/utils';

/** AppTopBar height (`h-13` = 52px) — the sticky offset the toolbar pins below. */
export const TOP_BAR_H = 52;
/**
 * Slack added to the offset so the capped table always clears the pinned
 * toolbar. It covers the flex gap between the toolbar and the table (~20px) plus
 * the content region's bottom padding (~py-8), which would otherwise let the
 * page over-scroll and tuck the table header a few px under the toolbar. Erring
 * a little large only leaves a hair of empty space at the bottom — never an
 * overlap — while `max-height` still lets short tables hug their content.
 */
const GAP = 36;

interface StickyTableToolbarProps {
  /** The screen's existing search / filters row — rendered unchanged. */
  children: React.ReactNode;
  className?: string;
}

/**
 * Pins a screen's search + filters row under the 52px app top bar so it stays
 * visible while the Title and KPIs scroll away, then lets the table below take
 * over with an internal row scroll (header + pagination pinned).
 *
 * It also publishes its own height as the `--dt-top-offset` CSS variable on
 * `:root`, which the sibling `DataTable` reads to cap its scroll area to the
 * height left in the viewport. A single sticky toolbar is mounted per route/tab
 * (one primary table per screen), so `:root` is a safe, plumbing-free channel
 * between these two siblings. The value is a transient layout measurement, so
 * it's written imperatively (no state / re-render).
 */
export function StickyTableToolbar({ children, className }: StickyTableToolbarProps): React.ReactElement {
  const ref = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const root = document.documentElement;
    const publish = (): void => {
      root.style.setProperty('--dt-top-offset', `${TOP_BAR_H + el.offsetHeight + GAP}px`);
    };
    publish();
    // jsdom (unit tests) doesn't implement ResizeObserver — the initial
    // publish() above still runs, later resizes just won't be tracked there.
    if (typeof ResizeObserver === 'undefined') {
      return () => root.style.removeProperty('--dt-top-offset');
    }
    const observer = new ResizeObserver(publish);
    observer.observe(el);
    return () => {
      observer.disconnect();
      root.style.removeProperty('--dt-top-offset');
    };
  }, []);

  // `top-13` = 52px (below AppTopBar). Opaque page background so rows scroll
  // cleanly underneath; z-20 keeps it under the top bar (z-30) but above rows.
  return (
    <div ref={ref} className={cn('sticky top-13 z-20 bg-background', className)}>
      {children}
    </div>
  );
}
